import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { comics, hotKeywords } from "@/mocks/comics";

const genres = ["热血战斗", "开挂主角", "逆袭流", "龙傲天", "打脸爽文"];

export default function Ranking() {
  const [genre, setGenre] = useState("热血战斗");

  const list = useMemo(() => {
    const filtered = comics.filter((c) => c.genre === genre);
    const sorted = [...filtered].sort((a, b) => b.hot - a.hot);
    return sorted.slice(0, 10);
  }, [genre]);

  const rankBadgeClass = (rank: number) =>
    rank === 1
      ? "bg-accent-500 text-background-50"
      : rank <= 3
      ? "bg-primary-500/20 text-primary-400"
      : "bg-background-200 text-foreground-600";

  const listJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${genre}排行`,
    itemListElement: list.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.title,
      url: `https://bencomic.github.io/comic/${c.id}`,
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(listJsonLd) }} />

      <Navbar />
      <main>
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "热血战斗排行" }]}
          title="热血战斗排行"
          subtitle="综合热度、评分、更新频率与读者口碑实时生成，拳拳到肉的战斗爽漫按人气排序，跟着榜单直接开刷，绝不踩雷。"
        />

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 榜单主体 */}
            <div className="lg:col-span-2">
              {/* 分类切换 */}
              <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="榜单类型">
                {genres.map((g) => (
                  <button
                    key={g}
                    type="button"
                    role="tab"
                    aria-selected={genre === g}
                    onClick={() => setGenre(g)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                      genre === g
                        ? "bg-primary-500 text-background-50 font-semibold"
                        : "bg-background-100 text-foreground-600 border border-background-300/50 hover:text-primary-400"
                    }`}
                  >
                    {g}排行
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">TOP {list.length} · {genre}</h2>
                <span className="text-xs text-foreground-600">今日 08:00 更新</span>
              </div>

              <ol className="flex flex-col gap-4">
                {list.map((c, idx) => (
                  <li key={c.id}>
                    <div className="flex gap-4 rounded-lg bg-background-100 border border-background-300/50 p-4 hover:border-primary-500/40 transition-colors">
                      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-heading text-xl md:text-2xl ${rankBadgeClass(idx + 1)}`}>
                        {idx + 1}
                      </span>
                      <Link to={`/comic/${c.id}`} className="shrink-0">
                        <img src={c.cover} alt={`${c.title}封面`} className="h-24 w-16 md:h-28 md:w-19 rounded-md object-cover object-top" />
                      </Link>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/comic/${c.id}`} className="font-heading text-lg md:text-xl text-foreground-950 hover:text-primary-400 transition-colors">{c.title}</Link>
                          <span className="inline-flex items-center gap-0.5 text-accent-400 text-sm"><i className="ri-star-fill text-xs"></i>{c.rating.toFixed(1)}</span>
                          <span className="text-xs text-foreground-700">{c.status}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {c.tags.map((t) => (
                            <span key={t} className="rounded-full bg-secondary-100/70 px-2 py-0.5 text-xs text-secondary-900">{t}</span>
                          ))}
                        </div>
                        <p className="mt-2 hidden sm:block text-sm leading-relaxed text-foreground-600 line-clamp-2">{c.desc}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-700">
                          <span>{c.chapters}话</span>
                          <span>{c.hot >= 10000 ? `${(c.hot / 10000).toFixed(1)}w` : c.hot}热度</span>
                          <span className="text-primary-400">{c.updateInfo}</span>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center shrink-0">
                        <Link
                          to={`/comic/${c.id}`}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
                        >
                          开刷<i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* 侧栏 */}
            <aside className="space-y-6">
              <div className="rounded-lg bg-background-100 border border-background-300/50 p-5">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">热搜词</h3>
                <div className="flex flex-wrap gap-2">
                  {hotKeywords.slice(0, 8).map((k, i) => (
                    <Link
                      key={k}
                      to={`/recommend?q=${encodeURIComponent(k)}`}
                      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs ${
                        i < 3 ? "bg-primary-500/15 text-primary-400 border border-primary-500/30" : "bg-background-50 text-foreground-600 border border-background-300/50"
                      }`}
                    >
                      {i < 3 && <i className="ri-fire-fill text-xs"></i>}
                      {k}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-300/50 p-5">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">编辑精选</h3>
                <div className="flex flex-col gap-3">
                  {comics.slice(0, 3).map((c) => (
                    <Link key={c.id} to={`/comic/${c.id}`} className="group flex gap-3 items-center">
                      <img src={c.cover} alt={`${c.title}封面`} className="h-16 w-12 rounded-md object-cover object-top" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-400 transition-colors">{c.title}</div>
                        <div className="text-xs text-foreground-600 mt-0.5">评分 {c.rating.toFixed(1)} · {c.chapters}话</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}